import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { RefreshCw, ArrowLeft } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Public status page — polls /api/status every 30s. Green/yellow/red lights
 * for API, DB, email queue, backup, scheduler. Zero auth. Linkable from
 * footer / bug reports so beta testers can self-check "is it me or the app?".
 */
export default function StatusPage() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      const [res, hist] = await Promise.all([
        axios.get(`${BACKEND}/api/status`, { timeout: 8000 }),
        axios.get(`${BACKEND}/api/status/incidents`, { timeout: 8000 }).catch(() => ({ data: { incidents: [] } })),
      ]);
      setData(res.data);
      setHistory((hist.data.incidents || []).filter(i => i.status === "resolved"));
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError(e.message || "Nie udało się pobrać statusu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const overall = data?.status;
  const bannerColor =
    overall === "ok" ? "bg-[#86EFAC]"
    : overall === "down" ? "bg-[#FCA5A5]"
    : "bg-[#E2E8F0]";
  const bannerLabel =
    overall === "ok" ? "✅ Aplikacja działa poprawnie"
    : overall === "down" ? "❌ Awaria — aplikacja niedostępna"
    : "⏳ Sprawdzam…";

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8" data-testid="status-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Link
            to="/"
            className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md px-3 py-1.5 font-bold text-sm inline-flex items-center gap-1"
            data-testid="status-back-link"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            Powrót
          </Link>
          <button
            onClick={load}
            className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md px-3 py-1.5 font-bold text-sm inline-flex items-center gap-1"
            data-testid="status-refresh-btn"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2.5} />
            Odśwież
          </button>
        </div>

        <h1 className="text-4xl md:text-5xl font-black mb-2 uppercase tracking-tight">
          Status systemu
        </h1>
        <p className="text-sm text-[#64748B] mb-6">
          Kartkówki Chemia · Aktualizacja co 30 sek. ·{" "}
          {lastRefresh ? `Ostatnie sprawdzenie: ${lastRefresh.toLocaleTimeString("pl")}` : "—"}
        </p>

        {/* Overall banner */}
        <div
          className={`brut border-4 border-[#0F172A] rounded-md p-6 mb-6 shadow-[8px_8px_0_#0F172A] ${bannerColor}`}
          data-testid="status-overall-banner"
        >
          <div className="text-2xl md:text-3xl font-black uppercase">{bannerLabel}</div>
          {data?.checked_at && (
            <div className="text-xs mt-2 font-bold">
              Sprawdzono: {new Date(data.checked_at).toLocaleString("pl")}
            </div>
          )}
        </div>

        {error && (
          <div
            className="brut border-2 border-[#0F172A] bg-[#FCA5A5] rounded-md p-4 mb-6 font-bold"
            data-testid="status-network-error"
          >
            ⚠️ Nie udało się połączyć z API: {error}
          </div>
        )}

        {/* Active incidents (admin-declared) */}
        {data?.active_incidents?.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="status-active-incidents">
            {data.active_incidents.map(inc => (
              <IncidentBox key={inc.id} inc={inc} />
            ))}
          </div>
        )}

        {/* Individual checks — admin-only. The public API `/status` returns
            only the overall verdict + active incidents; detailed diagnostics
            (uptime, DB errors, queue counts, backup age, scheduler jobs)
            are gated behind `/status/full` which requires require_admin.
            Removed from the public UI in Feb 2026 per user feedback: those
            metrics were only meaningful to admins. */}

        {/* 30-day history */}
        {history.length > 0 && (
          <div className="mt-8" data-testid="status-history">
            <h2 className="text-xl font-black uppercase mb-3">Historia (ostatnie 30 dni)</h2>
            <div className="space-y-2">
              {history.map(inc => (
                <div key={inc.id}
                     className="brut border-2 border-[#0F172A] rounded-md p-3 bg-white text-sm"
                     data-testid={`status-history-${inc.id}`}>
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <b>✅ {inc.title}</b>
                    <span className="text-xs text-[#64748B]">
                      {new Date(inc.created_at).toLocaleDateString("pl")} — {inc.resolved_at ? new Date(inc.resolved_at).toLocaleDateString("pl") : ""}
                    </span>
                  </div>
                  {inc.description && <div className="text-xs mt-1 text-[#334155]">{inc.description}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 text-xs text-[#64748B]">
          Widzisz problem? Napisz na <b>{process.env.REACT_APP_SUPPORT_EMAIL || "chemiainfo@kartkowka.pl"}</b> lub użyj przycisku „Feedback" w aplikacji.
        </div>
      </div>
    </div>
  );
}

const SEV_LABEL = {
  investigating: "🔍 Badamy",
  identified: "🔧 Przyczyna zidentyfikowana",
  monitoring: "👀 Monitorujemy",
  resolved: "✅ Rozwiązane",
};
const AFFECTED_LABEL = {
  api: "API", database: "MongoDB", email: "Kolejka e-mail",
  backup: "Backup", scheduler: "Zaplanowane zadania", other: "Inne",
};

function IncidentBox({ inc }) {
  return (
    <div
      className="brut border-4 border-[#0F172A] rounded-md p-4 bg-[#FEF08A] shadow-[6px_6px_0_#0F172A]"
      data-testid={`status-incident-${inc.id}`}
    >
      <div className="flex justify-between items-start flex-wrap gap-2 mb-2">
        <div>
          <div className="font-black text-lg">⚠️ {inc.title}</div>
          <div className="text-xs mt-0.5">
            <b>{AFFECTED_LABEL[inc.affected] || inc.affected}</b>
            {" · "}{SEV_LABEL[inc.severity] || inc.severity}
            {inc.eta_message && <> · ⏱ ETA: <b>{inc.eta_message}</b></>}
          </div>
        </div>
        <span className="text-xs font-bold">
          rozpoczęte: {new Date(inc.created_at).toLocaleString("pl")}
        </span>
      </div>
      {inc.description && (
        <div className="text-sm mb-2 text-[#334155]">{inc.description}</div>
      )}
      {Array.isArray(inc.updates) && inc.updates.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer font-bold" data-testid={`status-incident-timeline-toggle-${inc.id}`}>
            Timeline ({inc.updates.length} aktualizacj{inc.updates.length === 1 ? "a" : "i"})
          </summary>
          <div className="mt-2 space-y-1 pl-4 border-l-2 border-[#0F172A]/40">
            {inc.updates.slice().reverse().map((u, i) => (
              <div key={i} className="text-[#334155]">
                <span className="font-bold">{new Date(u.ts).toLocaleString("pl")}</span>
                {u.severity && <> · <b>{SEV_LABEL[u.severity] || u.severity}</b></>}
                <> — {u.message}</>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
